import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { BookOpen, ShieldCheck, User, ChevronRight, ImageIcon, Users } from "lucide-react";
import { PageHero, PageBody, Lightbox, slugify } from "./_shared";
import { usePoly } from "./PolyLayout";
import PhotoCarousel from "@/components/PhotoCarousel";

const GRADIENTS = [
  "from-[#003C6C] to-[#0a4d8f]",
  "from-teal-500 to-blue-600",
  "from-pink-500 to-purple-600",
  "from-emerald-500 to-teal-600",
  "from-orange-500 to-red-500",
  "from-indigo-500 to-purple-700",
  "from-rose-500 to-fuchsia-600",
  "from-[#003C6C] to-[#0a4d8f]",
];

export default function Departments() {
  const data = usePoly();
  // Already sorted by `order` asc on the backend, but defend just in case.
  // useMemo keeps the reference stable across renders — otherwise the sync-effect
  // below would fire on every render and override clicks on the sidebar tabs.
  const list = useMemo(
    () =>
      (data.departments || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [data.departments]
  );
  const allStaff = data.staff || [];
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  const queryDeptId = searchParams.get("d");
  const findBySlug = (s) =>
    s ? list.find((d) => slugify(d.name) === s.toLowerCase()) : null;
  const initialId =
    findBySlug(slug)?.id ||
    (queryDeptId && list.some((d) => d.id === queryDeptId)
      ? queryDeptId
      : list[0]?.id) ||
    null;
  const [activeId, setActiveId] = useState(initialId);
  const [lbIdx, setLbIdx] = useState(-1);

  // Sync selection when route slug / query param changes
  useEffect(() => {
    const bySlug = findBySlug(slug);
    if (bySlug) {
      setActiveId(bySlug.id);
      setLbIdx(-1);
      return;
    }
    if (queryDeptId && list.some((d) => d.id === queryDeptId)) {
      setActiveId(queryDeptId);
      setLbIdx(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, queryDeptId, list]);

  const active = list.find((d) => d.id === activeId) || null;
  const activeIdx = list.findIndex((d) => d.id === activeId);
  const grad = GRADIENTS[Math.max(0, activeIdx) % GRADIENTS.length];
  const photos = active?.photos || [];
  const isHodByDesignation = (s) => {
    const d = (s?.designation || "").toLowerCase();
    return /\b(hod|head\s+of\s+department)\b/i.test(d) || d.includes("head of department");
  };
  const deptStaff = allStaff
    .filter((s) => active && s.department_id === active.id)
    .sort((a, b) => {
      // 1. HOD by explicit link
      const hodId = active?.head_of_department_id;
      if (hodId) {
        if (a.id === hodId) return -1;
        if (b.id === hodId) return 1;
      }
      // 2. HOD by designation (fallback when explicit link is missing)
      const aH = isHodByDesignation(a);
      const bH = isHodByDesignation(b);
      if (aH && !bH) return -1;
      if (!aH && bH) return 1;
      // 3. Alphabetical by name
      return (a.name || "").localeCompare(b.name || "");
    });

  return (
    <div data-testid="poly-departments">
      <PageHero eyebrow="Academics" title="Departments" accent="teal" />
      <PageBody>
        {list.length === 0 ? (
          <div className="text-center bg-white rounded-xl shadow p-12 text-slate-500">
            No active departments are published right now. Please check back soon.
          </div>
        ) : (
          <div className="grid md:grid-cols-[300px_1fr] gap-6">
            {/* Left: list of departments */}
            <aside
              className="bg-white rounded-xl shadow-md overflow-hidden self-start"
              data-testid="poly-departments-list"
            >
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#003C6C] font-semibold">
                  Browse
                </div>
                <div className="font-serif text-lg text-slate-900">
                  All Departments ({list.length})
                </div>
              </div>
              <ul>
                {list.map((d, i) => {
                  const isActive = d.id === active?.id;
                  const itemGrad = GRADIENTS[i % GRADIENTS.length];
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveId(d.id);
                          setLbIdx(-1);
                        }}
                        data-testid={`poly-dept-tab-${d.id}`}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                          isActive
                            ? "bg-[#FFF8DC] border-[#FDC700]"
                            : "border-transparent hover:bg-slate-50"
                        }`}
                      >
                        <span
                          className={`w-8 h-8 rounded-md bg-gradient-to-br ${itemGrad} text-white flex items-center justify-center shrink-0`}
                        >
                          <BookOpen size={14} strokeWidth={1.5} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span
                            className={`block text-sm leading-snug ${
                              isActive
                                ? "text-slate-900 font-semibold"
                                : "text-slate-700"
                            }`}
                          >
                            {d.name}
                          </span>
                          {d.code && (
                            <span className="block text-[10px] text-slate-500 font-mono uppercase tracking-wider mt-0.5">
                              {d.code}
                            </span>
                          )}
                        </span>
                        <ChevronRight
                          size={14}
                          strokeWidth={1.5}
                          className={isActive ? "text-[#003C6C]" : "text-slate-300"}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Right: detail */}
            {active && (
              <article
                key={active.id}
                data-testid={`poly-dept-${active.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-md"
              >
                <div
                  className={`bg-gradient-to-br ${grad} text-white p-6 relative overflow-hidden`}
                >
                  <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-md bg-white/20 backdrop-blur flex items-center justify-center text-white mb-3">
                      <BookOpen size={20} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-serif text-2xl sm:text-3xl leading-tight">
                      {active.name}
                    </h2>
                    {active.code && (
                      <div className="mt-1 text-xs uppercase tracking-wider opacity-80 font-mono">
                        {active.code}
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-6 sm:p-7">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {active.course_type && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm bg-[#FFF1AA] text-[#003C6C] font-semibold">
                        {active.course_type}
                      </span>
                    )}
                    {active.approval_body && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm bg-[#FFF1AA] text-slate-900 font-semibold">
                        <ShieldCheck size={10} strokeWidth={2} />
                        {active.approval_body}
                      </span>
                    )}
                  </div>
                  {active.head_of_department && (
                    <div className="flex items-center gap-2 text-sm text-slate-700 mb-4">
                      <User size={14} strokeWidth={1.5} className="text-[#003C6C]" />
                      <span>
                        <span className="text-slate-500">HoD:</span> {active.head_of_department}
                      </span>
                    </div>
                  )}

                  {/* Description + Photos — side by side */}
                  <div
                    className={`grid gap-6 ${photos.length > 0 ? "lg:grid-cols-[1fr_auto]" : "grid-cols-1"}`}
                  >
                    <div className="min-w-0">
                      {active.about_html ? (
                        <div
                          className="prose prose-sm prose-slate max-w-none text-slate-700"
                          dangerouslySetInnerHTML={{ __html: active.about_html }}
                        />
                      ) : (
                        <div className="text-sm text-slate-400 italic">
                          Department details coming soon.
                        </div>
                      )}
                    </div>

                    {photos.length > 0 && (
                      <div className="lg:w-[400px] shrink-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[#003C6C] font-semibold mb-2 flex items-center gap-1.5">
                          <ImageIcon size={11} strokeWidth={1.5} />
                          Glimpses ({photos.length})
                        </div>
                        <PhotoCarousel
                          photos={photos}
                          onPhotoClick={(idx) => setLbIdx(idx)}
                          name={active.name}
                          testidPrefix="mitest-dept-photo"
                          accentClass="bg-blue-500"
                        />
                      </div>
                    )}
                  </div>

                  {/* Department staff */}
                  {deptStaff.length > 0 && (
                    <div className="mt-8">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#003C6C] font-semibold mb-3 flex items-center gap-1.5">
                        <Users size={11} strokeWidth={1.5} />
                        Faculty & Staff ({deptStaff.length})
                      </div>
                      <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {deptStaff.map((s) => {
                          const isHOD = active?.head_of_department_id === s.id || isHodByDesignation(s);
                          return (
                          <li
                            key={s.id}
                            data-testid={`poly-dept-staff-${s.id}`}
                            className={`bg-slate-50 rounded-lg p-3 flex items-center gap-3 ${
                              isHOD ? "ring-2 ring-[#FDC700] bg-[#FFF8DC]" : ""
                            }`}
                          >
                            {s.photo_url ? (
                              <img
                                src={s.photo_url}
                                alt={s.name}
                                className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-600 shrink-0">
                                <User size={18} strokeWidth={1.5} />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                                {s.name}
                                {isHOD && (
                                  <span
                                    data-testid={`poly-dept-hod-badge-${s.id}`}
                                    className="inline-block text-[9px] tracking-[0.15em] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-[#FDC700] text-[#003C6C] shrink-0"
                                  >
                                    HOD
                                  </span>
                                )}
                              </div>
                              {s.designation && (
                                <div className="text-[11px] text-slate-500 truncate">
                                  {s.designation}
                                </div>
                              )}
                            </div>
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </PageBody>
      {active && lbIdx >= 0 && photos.length > 0 && (
        <Lightbox
          photos={photos.map((p) => ({ image_url: p.url, caption: active.name }))}
          idx={lbIdx}
          onIdx={setLbIdx}
          onClose={() => setLbIdx(-1)}
        />
      )}
    </div>
  );
}
